import { Button } from "@aurem/ui";
import type { FC, ReactNode } from "react";
import { Component } from "react";

type ErrorContentProps = {
  heading: string;
  description: string;
  resetLabel: string;
  resetCb: () => void;
};

const ErrorContent: FC<ErrorContentProps> = ({
  heading,
  description,
  resetLabel,
  resetCb,
}) => {
  return (
    <div className="flex min-h-screen w-full items-center justify-center text-center">
      <div>
        <div className="my-6 rounded-lg border border-danger-400 bg-danger-100 shadow-md max-sm:rounded-none">
          <div className="flex flex-col items-center gap-4 p-12">
            <h2 className="mb-2 text-lg font-medium">{heading}</h2>
            <p className="text-danger-600">{description}</p>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <Button
            className="flex-1"
            variant="outlined"
            color="destructive"
            onClick={resetCb}
          >
            {resetLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

type ErrorBoundaryProps = {
  children: ReactNode;
  logError: (error: unknown) => void;
  heading?: string;
  description?: string;
  resetLabel?: string;
};

type ErrorBoundaryState = {
  error: Error | null;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    this.props.logError(error);
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorContent
          heading={this.props.heading ?? "Unexpected error"}
          description={this.props.description ?? this.state.error.message}
          resetLabel={this.props.resetLabel ?? "Retry"}
          resetCb={() => this.setState({ error: null })}
        />
      );
    } else {
      return this.props.children;
    }
  }
}
